/**
 * A self-declared invoice for a farmer's sale (owner, 2026-09-27: "for farmer, need to have option to
 * upload and generate invoice (URD - Self declared invoice)").
 *
 * A farmer selling their own produce is usually not registered under GST - an unregistered dealer
 * (URD) - and has no invoice book. The buyer still needs a document for the goods and the payment,
 * and an FSC or EUDR claim has to be on it. This is that document: a bill of supply from an
 * unregistered supplier, built from the sale as recorded, with the supplier's declaration and a
 * place for their signature or thumb impression. It is HTML, printed or saved as PDF from the
 * browser, as the consent documents are (Indic names render there; a server-side PDF would not).
 * The wording of the declaration is the organisation's to confirm with its tax adviser.
 */

export interface UrdInvoiceInput {
  invoiceNo: string;
  invoiceDate: string;
  organisation: string;
  supplier: { name: string; code?: string | null; address?: string | null; phone?: string | null; pan?: string | null };
  buyer: { name: string; gstin?: string | null; address?: string | null };
  goods: { description: string; species?: string | null; hsn?: string | null; quantity: number; unit: string; rate?: number | null; amount: number };
  plot?: string | null;
  harvest?: string | null;
  claims?: string | null;
  /**
   * Each claim with the certificate it is made under (owner, 2026-09-27: "we need to have claims ...
   * Also the certificate code is required from scope of the plot"): printed as a table, the way a
   * chain-of-custody auditor reads a sales document.
   */
  claimLines?: Array<{ claim: string; certificate: string | null }>;
  transitPermitNo?: string | null;
  vehicleNo?: string | null;
  tradeCode?: string | null;
  /**
   * Each side's OTP confirmation, as recorded (owner, 2026-09-27: "this sale invoice also requires
   * OTPs from both parties"): the channels and masked contacts, and when.
   */
  confirmations?: { supplier?: { via: string; at: string } | null; buyer?: { via: string; at: string } | null };
}

const stamp = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * The line that says how the record was validated (owner, 2026-09-27: "Add disclaimer 'This is
 * digitally generated record, validated via <channel details> with date and time stamp'").
 */
export function validationLine(confirmations: UrdInvoiceInput['confirmations']): string {
  const parts = [
    confirmations?.supplier ? `${confirmations.supplier.via} (supplier, ${stamp(confirmations.supplier.at)})` : null,
    confirmations?.buyer ? `${confirmations.buyer.via} (buyer, ${stamp(confirmations.buyer.at)})` : null,
  ].filter(Boolean);
  if (parts.length === 0) return 'Not yet confirmed: the supplier and the buyer each confirm this invoice with a one-time code (OTP).';
  const waiting = !confirmations?.supplier ? ' Awaiting the supplier\'s confirmation.' : !confirmations?.buyer ? ' Awaiting the buyer\'s confirmation.' : '';
  return `This is a digitally generated record, validated via ${parts.join(' and ')} by one-time code (OTP).${waiting}`;
}

const escape = (text: unknown) => String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen',
  'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function belowHundred(n: number): string {
  return n < 20 ? ONES[n]! : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`;
}
function belowThousand(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  return [hundreds ? `${ONES[hundreds]} Hundred` : '', rest ? belowHundred(rest) : ''].filter(Boolean).join(' ');
}

/** Rupees in words, in the Indian system (lakh, crore): 125000.5 -> "One Lakh Twenty Five Thousand Rupees and Fifty Paise Only". */
export function rupeesInWords(amount: number): string {
  const rupees = Math.floor(Math.abs(amount));
  const paise = Math.round((Math.abs(amount) - rupees) * 100);
  const parts: string[] = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(`${belowThousand(crore)} Crore`);
  if (lakh) parts.push(`${belowHundred(lakh)} Lakh`);
  if (thousand) parts.push(`${belowHundred(thousand)} Thousand`);
  if (rest) parts.push(belowThousand(rest));
  const words = parts.length ? parts.join(' ') : 'Zero';
  return `${words} Rupees${paise ? ` and ${belowHundred(paise)} Paise` : ''} Only`;
}

const money = (value: number | null | undefined) =>
  value == null ? '--' : `₹ ${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function buildUrdInvoiceHtml(input: UrdInvoiceInput): string {
  const row = (label: string, value: unknown) => (value ? `<tr><th>${escape(label)}</th><td>${escape(value)}</td></tr>` : '');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escape(input.invoiceNo)}</title>
<style>
  body { font-family: "Noto Sans", "Noto Sans Devanagari", "Noto Sans Bengali", Arial, sans-serif; color: #111; margin: 16px auto; background: #e5e7eb; font-size: 13px; }
  h1 { font-size: 18px; margin: 0 0 2px; text-align: center; letter-spacing: .5px; }
  .sub { text-align: center; color: #555; margin: 0 0 18px; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 14px; }
  .box { border: 1px solid #999; padding: 10px 12px; }
  .box h2 { font-size: 12px; text-transform: uppercase; color: #555; margin: 0 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  .meta th, .meta td { text-align: left; padding: 3px 6px; font-weight: normal; }
  .meta th { color: #555; width: 34%; vertical-align: top; }
  .goods th, .goods td { border: 1px solid #999; padding: 6px 8px; }
  .goods th { background: #f2f2f2; font-size: 12px; }
  .num { text-align: right; }
  .words { margin: 8px 0 14px; }
  .declaration { border: 1px solid #999; padding: 10px 12px; margin-top: 14px; }
  .sign { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 40px; }
  .sign div { border-top: 1px solid #333; padding-top: 6px; text-align: center; color: #333; }
  .thumb { width: 90px; height: 90px; border: 1px dashed #999; margin: 0 auto 6px; }
  .page { width: 794px; min-height: 1123px; margin: 0 auto; padding: 40px 44px; background: #fff; box-sizing: border-box; }
  .digital { margin-top: 18px; padding: 8px 10px; border: 1px solid #15803d; background: #f0fdf4; font-size: 12px; }
  .digital.pending { border-color: #d97706; background: #fffbeb; }
  @media print { body { margin: 0; padding: 0; max-width: none; } .page { padding: 12mm; } }
</style></head><body><section class="page">
<h1>BILL OF SUPPLY - SELF-DECLARED INVOICE</h1>
<p class="sub">Supply by an unregistered person (URD) &middot; prepared through ${escape(input.organisation)}</p>
<div class="parties">
  <div class="box"><h2>Supplier (farmer)</h2><table class="meta">
    ${row('Name', input.supplier.name)}${row('Member code', input.supplier.code)}${row('Address', input.supplier.address)}
    ${row('Phone', input.supplier.phone)}${row('PAN', input.supplier.pan)}<tr><th>GST status</th><td>Unregistered (URD)</td></tr>
  </table></div>
  <div class="box"><h2>Buyer</h2><table class="meta">
    ${row('Name', input.buyer.name)}${row('Address', input.buyer.address)}${row('GSTIN', input.buyer.gstin)}
  </table></div>
</div>
<table class="meta" style="margin-bottom:12px">
  ${row('Invoice no.', input.invoiceNo)}${row('Invoice date', input.invoiceDate)}${row('Sale record', input.tradeCode)}
  ${row('From plot', input.plot)}${row('From harvest', input.harvest)}${input.claimLines?.length ? '' : row('Claims', input.claims)}
  ${row('Transit permit no.', input.transitPermitNo)}${row('Vehicle no.', input.vehicleNo)}
</table>
<table class="goods">
  <thead><tr><th>#</th><th>Description of goods</th><th>HSN</th><th class="num">Quantity</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead>
  <tbody><tr><td>1</td><td>${escape(input.goods.description)}${input.goods.species ? ` - ${escape(input.goods.species)}` : ''}</td>
    <td>${escape(input.goods.hsn ?? '')}</td><td class="num">${escape(input.goods.quantity)} ${escape(input.goods.unit)}</td>
    <td class="num">${money(input.goods.rate)}</td><td class="num">${money(input.goods.amount)}</td></tr></tbody>
  <tfoot><tr><th colspan="5" class="num">Total</th><th class="num">${money(input.goods.amount)}</th></tr></tfoot>
</table>
${input.claimLines?.length ? `<table class="goods" style="margin-top:10px">
  <thead><tr><th>Claim on these goods</th><th>Certificate / reference code</th></tr></thead>
  <tbody>${input.claimLines.map((line) => `<tr><td>${escape(line.claim)}</td><td>${escape(line.certificate ?? '--')}</td></tr>`).join('')}</tbody>
</table>` : ''}
<p class="words"><strong>Amount in words:</strong> ${escape(rupeesInWords(input.goods.amount))}</p>
<div class="declaration"><strong>Declaration by the supplier</strong><br>
  I declare that I am not registered under the Goods and Services Tax Act, that the goods described above are produce of my own
  land${input.plot ? ` (${escape(input.plot)})` : ''}, grown and harvested by me, and that the particulars given above are true and correct.
  No tax is charged on this bill.</div>
${input.confirmations?.supplier && input.confirmations?.buyer ? '' : `<div class="sign">
  <div><div class="thumb"></div>Signature / thumb impression of the supplier</div>
  <div style="padding-top:102px">Received by (buyer)</div>
</div>`}
<div class="digital${input.confirmations?.supplier && input.confirmations?.buyer ? '' : ' pending'}">${escape(validationLine(input.confirmations))}</div>
</section></body></html>`;
}
